import type React from "react";
import { css } from "../../../styled-system/css";
import { Box } from "../../../styled-system/jsx";
import InsightCard, { type InsightData } from "./InsightCard";
import { useInsights, useMarkInsightViewed, useDismissInsight } from "../../hooks/useInsights";
import Section from "./Section";
import { useTranslation } from "react-i18next";

interface InsightsFeedProps {
  onStartNewConversation?: (message: Record<string, unknown>) => void;
}

const InsightsFeed: React.FC<InsightsFeedProps> = ({ onStartNewConversation }) => {
  const { t } = useTranslation(["chat"]);
  const { data: insightsData, isLoading } = useInsights({
    dismissed: "false",
    pageSize: 10,
  });

  const markViewed = useMarkInsightViewed();
  const dismissInsight = useDismissInsight();

  const handleView = async (insightId: number) => {
    await markViewed(insightId);
  };

  const handleDismiss = async (insightId: number) => {
    await dismissInsight(insightId);
  };

  const insights = insightsData?.results || [];
  const insightCount = insightsData?.count || 0;

  return (
    <Section label={t(($) => $.chat["Insights"])} count={insightCount}>
      {isLoading && (
        <Box
          className={css({
            padding: "20px",
            color: "#6b7280",
            fontSize: "14px",
            textAlign: "center",
            fontStyle: "italic",
          })}
        >
          {t(($) => $.chat["Loading insights..."])}
        </Box>
      )}
      {!isLoading && insights.length === 0 ?
        <Box
          className={css({
            padding: "20px",
            color: "#6b7280",
            fontSize: "14px",
            textAlign: "center",
            fontStyle: "italic",
          })}
        >
          {t(($) => $.chat["No insights available"])}
        </Box>
      : insights.map((insight) => (
          <InsightCard
            key={insight.id}
            insight={insight as InsightData}
            onView={async () => await handleView(insight.id)}
            onDismiss={async () => await handleDismiss(insight.id)}
            onStartNewConversation={onStartNewConversation}
          />
        ))
      }
    </Section>
  );
};

export default InsightsFeed;
